import React, { useState, useEffect, useCallback } from 'react';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"; // Assuming shadcn/ui command is available
import { useSearch } from '@/museum-app/context/search-context';
import { useDebounce } from '@/museum-app/hooks/use-debounce'; // Assuming a debounce hook exists or create one
import { useSettings } from '@/museum-app/context/settings-context';
import { useNavigate } from 'react-router';
import axios from 'axios'; // Or your preferred API client
import { Building, Image } from 'lucide-react'; // Icons for result types

interface SearchResult {
  id: number;
  type: 'room' | 'exhibit'; // Add other types like 'tour' if needed
  title: string;
  url: string;
}

const GlobalSearchOverlay: React.FC = () => {
  const { isOpen, closeSearch } = useSearch();
  const { language } = useSettings();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300); // Debounce input by 300ms

  const fetchResults = useCallback(async (searchQuery: string, lang: string) => {
    if (!searchQuery || searchQuery.trim().length < 2) { // Minimum query length
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      // Adjust API endpoint and params as needed
      const response = await axios.get<{ data: SearchResult[] }>('/api/v1/search', {
        params: { q: searchQuery, lang: lang },
      });
      setResults(response.data.data || []);
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]); // Clear results on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResults(debouncedQuery, language);
  }, [debouncedQuery, language, fetchResults]);

  const handleSelectResult = (url: string) => {
    closeSearch();
    navigate(url);
    setQuery(''); // Clear query after navigation
  };

  // Need to reset query when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);


  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && closeSearch()}>
      <CommandInput
        placeholder="Search rooms, exhibits..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && <CommandEmpty>Loading...</CommandEmpty>}
        {!isLoading && !results.length && debouncedQuery.length > 1 && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
        {!isLoading && results.length > 0 && (
          <CommandGroup heading="Results">
            {results.map((result) => (
              <CommandItem
                key={`${result.type}-${result.id}`}
                value={`${result.type}-${result.title}`} // Unique value for cmdk
                onSelect={() => handleSelectResult(result.url)}
                className="flex items-center gap-2"
              >
                {result.type === 'room' && <Building className="h-4 w-4 text-muted-foreground" />}
                {result.type === 'exhibit' && <Image className="h-4 w-4 text-muted-foreground" />}
                <span>{result.title}</span>
                <span className="ml-auto text-xs text-muted-foreground capitalize">{result.type}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearchOverlay;
